/**
 * Wire contracts.
 *
 * Every request body the API accepts and every entity it returns is described
 * once, here. Fastify validates inbound with these; the generated client parses
 * outbound with the same objects; the web and mobile forms derive their types
 * from them. A field can therefore not exist on the server and be missing on
 * the client — there is nowhere for the two to disagree.
 */
import { z } from 'zod';
import { JOB_STATUSES } from './job-status.js';
import { ACCESS_REASON_LABELS } from './subscription.js';
/**
 * What the signed-in company may do, as it travels over the wire.
 *
 * The reasons are taken from `ACCESS_REASON_LABELS` rather than written out
 * again, so a reason added in subscription.ts cannot fail to parse here — the
 * failure mode being a client that rejects a perfectly good session because
 * the server learned a new word.
 */
export const companyAccessSchema = z.object({
    level: z.enum(['full', 'read_only']),
    reason: z.enum(Object.keys(ACCESS_REASON_LABELS)),
    /** When it changes on its own, ISO. Null when nothing is scheduled. */
    changesAt: z.string().nullable(),
});
import { FIELD_CACHE_BACK_DAYS, FIELD_CACHE_DAYS, FIELD_CACHE_MAX_DAYS, MUTATION_KINDS, } from './sync.js';
import { CALL_DIRECTIONS, CALL_STATUSES, MESSAGE_DIRECTIONS, MESSAGE_STATUSES, } from './telephony.js';
// Display labels for these enums live in labels.ts, which is free of the zod
// import so a client that only renders a word does not bundle a validator.
// --- primitives -------------------------------------------------------------
export const idSchema = z.string().min(1);
export const isoDateTime = z.string().datetime({ offset: true }).or(z.string().datetime());
export const centsSchema = z.number().int().safe();
export const nonNegativeCents = centsSchema.min(0);
export const bpsSchema = z.number().int().min(0).max(100_000);
export const quantitySchema = z.number().finite().min(0).max(1_000_000);
export const paginationSchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
// --- enums ------------------------------------------------------------------
export const userRoleSchema = z.enum(['admin', 'dispatcher', 'tech']);
export const jobStatusSchema = z.enum(JOB_STATUSES);
export const jobPrioritySchema = z.enum(['low', 'normal', 'high', 'emergency']);
export const estimateStatusSchema = z.enum(['draft', 'sent', 'approved', 'declined', 'expired']);
export const invoiceStatusSchema = z.enum(['draft', 'sent', 'paid', 'void']);
export const paymentMethodSchema = z.enum(['cash', 'check', 'card', 'ach', 'other']);
export const discountTypeSchema = z.enum(['none', 'fixed', 'percent']);
/* Same members as a discount, different meaning — see shared/deposits.ts. */
export const depositTypeSchema = z.enum(['none', 'fixed', 'percent']);
export const lineItemKindSchema = z.enum(['labor', 'material', 'fee', 'discount']);
export const noteVisibilitySchema = z.enum(['internal', 'customer']);
export const attachmentKindSchema = z.enum(['photo', 'signature', 'document']);
export const recurrenceFrequencySchema = z.enum(['weekly', 'biweekly', 'monthly', 'custom_days']);
// --- auth -------------------------------------------------------------------
export const loginRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
    /** Optional device registration folded into login so mobile does one call. */
    device: z
        .object({
        platform: z.enum(['ios', 'android', 'web']),
        pushToken: z.string().optional(),
        name: z.string().optional(),
    })
        .optional(),
});
export const refreshRequestSchema = z.object({ refreshToken: z.string().min(1) });
/**
 * Creating a company and its first admin, in one form.
 *
 * Shapes only. Whether a password is *good enough* is decided by
 * `signupProblems` in credentials.ts, so the browser and the server reach the
 * same verdict and the message a person reads is written once.
 */
export const signupRequestSchema = z.object({
    companyName: z.string().min(1).max(120),
    name: z.string().min(1).max(120),
    email: z.string().min(3).max(254),
    password: z.string().min(1).max(200),
});
export const forgotPasswordRequestSchema = z.object({ email: z.string().min(1).max(254) });
export const resetPasswordRequestSchema = z.object({
    token: z.string().min(1).max(200),
    password: z.string().min(1).max(200),
});
export const publicUserSchema = z.object({
    id: idSchema,
    email: z.string().email(),
    name: z.string(),
    role: userRoleSchema,
    phone: z.string().nullable(),
    colorHex: z.string().nullable(),
    active: z.boolean(),
    /*
     * Whether this person may type a card number in and charge it.
     *
     * Only meaningful for technicians — office roles carry it with the job. See
     * card-permission.ts for the rule itself, which the server and the field
     * app both read from there rather than each deciding for themselves.
     */
    canTakePayment: z.boolean(),
    /**
     * Whether this person is sent out on jobs.
     *
     * Only consulted for office roles — a technician always is. Use
     * `isAssignable` rather than reading this directly, so the picker, the
     * board and the server cannot disagree about who counts.
     */
    goesOnJobs: z.boolean(),
    /**
     * Crewlark staff. Sent so the app knows whether to offer the admin screen.
     *
     * Not a permission: the server checks the database on every admin request
     * and does not trust this. It only decides whether a menu item is drawn.
     */
    platformOwner: z.boolean().optional(),
});
/**
 * The company as a signed-in session knows it.
 *
 * Exactly what `serializeCompany` returns, declared once. Three copies of this
 * shape existed — here, in the API client's `/auth/me` signature, and again in
 * the web app's auth context — and adding a currency to one of them broke the
 * build on the other two.
 */
export const sessionCompanySchema = z.object({
    id: idSchema,
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    taxRateBps: bpsSchema,
    timezone: z.string(),
    /** Every amount the app renders is formatted in it. */
    currency: z.string(),
    /** Pre-joined for display; the editable parts live on the company profile. */
    address: z.string(),
    /**
     * Whether this company may change anything, and why.
     *
     * On the session rather than fetched separately because every screen needs
     * it and none of them should have to ask: a banner that appears a moment
     * after the page, or a Save button that is enabled until the server refuses
     * it, is worse than no warning at all.
     */
    access: companyAccessSchema,
    /**
     * Whether this company has the phone.
     *
     * On the session for the same reason `access` is: the navigation is built
     * before anything else loads, and a Calls tab that appears and then vanishes
     * is worse than one that was never there.
     */
    telephonyEnabled: z.boolean(),
});
/**
 * One company, as the platform owner's list shows it.
 *
 * Deliberately thin. It carries standing, headcount and what that comes to —
 * everything needed to decide whether to extend, suspend or leave alone — and
 * nothing about the company's own customers, jobs or money. See the note at
 * the top of apps/api/src/routes/admin.ts.
 */
export const adminCompanySchema = z.object({
    id: idSchema,
    name: z.string(),
    email: z.string().nullable(),
    createdAt: z.string(),
    planStatus: z.enum(['trialing', 'active', 'past_due', 'canceled']),
    trialEndsAt: z.string().nullable(),
    suspendedAt: z.string().nullable(),
    suspendedReason: z.string().nullable(),
    seats: z.number().int(),
    monthlyCents: z.number().int(),
    access: z.enum(['full', 'read_only']),
    reason: companyAccessSchema.shape.reason,
    /** Whether the dialer tier is switched on for them. */
    telephonyEnabled: z.boolean(),
});
export const authResponseSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    /** Seconds until the access token expires; the client refreshes early. */
    expiresIn: z.number().int(),
    user: publicUserSchema,
    company: sessionCompanySchema,
});
// --- customers & properties -------------------------------------------------
export const propertySchema = z.object({
    id: idSchema,
    customerId: idSchema,
    label: z.string().nullable(),
    line1: z.string(),
    line2: z.string().nullable(),
    city: z.string(),
    state: z.string(),
    postalCode: z.string(),
    notes: z.string().nullable(),
    gateCode: z.string().nullable(),
    isPrimary: z.boolean(),
    /**
     * Where this address is, if anyone has worked it out.
     *
     * Null means not placed — not looked up yet, or the geocoder could not make
     * sense of it. Both are the same to a map: the phones fall back to the
     * device's own geocoder, and the web map leaves the pin off rather than
     * inventing one.
     */
    latitude: z.number().nullable().default(null),
    longitude: z.number().nullable().default(null),
    createdAt: isoDateTime,
});
export const propertyInputSchema = z.object({
    label: z.string().max(80).nullish(),
    line1: z.string().min(1).max(200),
    line2: z.string().max(200).nullish(),
    city: z.string().min(1).max(120),
    state: z.string().min(1).max(60),
    postalCode: z.string().min(1).max(20),
    notes: z.string().max(2000).nullish(),
    gateCode: z.string().max(40).nullish(),
    isPrimary: z.boolean().optional(),
});
/**
 * A list the office keeps for itself.
 *
 * Named by them, not by us: "Yard sign", "Nextdoor", "Drain clearing". Two
 * lists today — where work came from, and what kind of work it is — sharing
 * one shape because they are the same thing with a different label on the tab.
 */
export const catalogKindSchema = z.enum(['lead_source', 'job_type']);
export const catalogTermSchema = z.object({
    id: idSchema,
    kind: catalogKindSchema,
    name: z.string(),
    archivedAt: isoDateTime.nullable(),
    /** How many jobs point at it — the reason removing is archiving. */
    useCount: z.number().int().optional(),
});
export const catalogTermInputSchema = z.object({
    kind: catalogKindSchema,
    name: z.string().min(1).max(80),
});
export const customerSchema = z.object({
    id: idSchema,
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    notes: z.string().nullable(),
    createdAt: isoDateTime,
    properties: z.array(propertySchema).optional(),
    /** Denormalised for list views; the server computes them. */
    jobCount: z.number().int().optional(),
    outstandingCents: centsSchema.optional(),
    /**
     * How their most recent job was classified. Lead source and job type belong
     * to a job, so a customer's is the last job's — stated as such rather than
     * pretending the customer has one of their own.
     */
    latestJob: z
        .object({
        number: z.number().int(),
        leadSourceName: z.string().nullable(),
        jobTypeName: z.string().nullable(),
    })
        .nullable()
        .optional(),
});
export const customerInputSchema = z.object({
    name: z.string().min(1).max(200),
    email: z.string().email().nullish().or(z.literal('')),
    phone: z.string().max(40).nullish(),
    notes: z.string().max(4000).nullish(),
    /** Create the first property inline so the office types one form, not two. */
    property: propertyInputSchema.optional(),
});
export const customerSearchSchema = paginationSchema.extend({
    /** Matches name, phone (digits only), email, or any property address. */
    q: z.string().optional(),
});
// --- price book -------------------------------------------------------------
export const priceBookItemSchema = z.object({
    id: idSchema,
    code: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    kind: lineItemKindSchema,
    unitPriceCents: centsSchema,
    unit: z.string().nullable(),
    taxable: z.boolean(),
    active: z.boolean(),
});
export const priceBookItemInputSchema = z.object({
    code: z.string().max(40).nullish(),
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullish(),
    kind: lineItemKindSchema.default('labor'),
    unitPriceCents: centsSchema,
    unit: z.string().max(20).nullish(),
    taxable: z.boolean().default(true),
    active: z.boolean().default(true),
});
// --- line items -------------------------------------------------------------
export const lineItemSchema = z.object({
    id: idSchema,
    priceBookItemId: idSchema.nullable(),
    name: z.string(),
    description: z.string().nullable(),
    kind: lineItemKindSchema,
    quantity: quantitySchema,
    unitPriceCents: centsSchema,
    taxable: z.boolean(),
    position: z.number().int(),
    /** Server-computed via computeTotals; never sent by a client. */
    totalCents: centsSchema.optional(),
});
export const lineItemInputSchema = z.object({
    priceBookItemId: idSchema.nullish(),
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullish(),
    kind: lineItemKindSchema.default('labor'),
    quantity: quantitySchema.default(1),
    unitPriceCents: centsSchema,
    taxable: z.boolean().default(true),
    position: z.number().int().min(0).optional(),
});
// --- notes & attachments ----------------------------------------------------
export const noteSchema = z.object({
    id: idSchema,
    jobId: idSchema.nullable(),
    customerId: idSchema.nullable(),
    authorId: idSchema.nullable(),
    authorName: z.string().nullable(),
    body: z.string(),
    visibility: noteVisibilitySchema,
    createdAt: isoDateTime,
});
export const noteInputSchema = z.object({
    body: z.string().min(1).max(8000),
    visibility: noteVisibilitySchema.default('internal'),
    /** Present when the note was written offline; preserves the real timestamp. */
    clientCreatedAt: isoDateTime.optional(),
});
export const attachmentSchema = z.object({
    id: idSchema,
    jobId: idSchema.nullable(),
    kind: attachmentKindSchema,
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int(),
    url: z.string(),
    caption: z.string().nullable(),
    uploadedById: idSchema.nullable(),
    createdAt: isoDateTime,
});
// --- jobs -------------------------------------------------------------------
export const jobStatusEventSchema = z.object({
    id: idSchema,
    jobId: idSchema,
    fromStatus: jobStatusSchema.nullable(),
    toStatus: jobStatusSchema,
    actorId: idSchema.nullable(),
    actorName: z.string().nullable(),
    /** 'web' | 'mobile' | 'system' | 'offline_sync' */
    source: z.string(),
    note: z.string().nullable(),
    createdAt: isoDateTime,
});
/**
 * What a job carries about its invoice.
 *
 * Deliberately not the whole invoice: a phone that has cached a job needs the
 * number to read aloud and the balance to collect, and has no business
 * holding a customer's billing history in a van.
 */
export const jobInvoiceSummarySchema = z.object({
    id: idSchema,
    number: z.number().int(),
    status: invoiceStatusSchema,
    totalCents: centsSchema,
    amountPaidCents: centsSchema,
    balanceDueCents: centsSchema,
});
/**
 * An estimate as a job shows it.
 *
 * A job can carry several — "while you're here, what about the guttering"
 * happens more than once — so this is the row, not the document: enough to
 * list what was quoted and what became of it, and an id to open the rest.
 */
export const jobEstimateSummarySchema = z.object({
    id: idSchema,
    number: z.number().int(),
    status: estimateStatusSchema,
    title: z.string(),
    totalCents: centsSchema,
    createdById: idSchema.nullable(),
    createdAt: isoDateTime,
});
/** One line of the internal cost sheet. Never leaves the office. */
export const jobCostItemSchema = z.object({
    id: idSchema,
    jobId: idSchema,
    name: z.string(),
    quantity: z.number(),
    unitCostCents: centsSchema,
    note: z.string().nullable(),
    position: z.number().int(),
    createdAt: isoDateTime,
});
export const jobCostItemInputSchema = z.object({
    name: z.string().min(1).max(200),
    quantity: z.number().min(0).max(100_000).default(1),
    unitCostCents: centsSchema,
    note: z.string().max(500).nullish(),
});
/**
 * One row of the answer to "what can I do to this job next".
 *
 * The server works these out from the transition table and its own view of
 * the job — whether an invoice exists, what is still owed — because only the
 * server can see all of that. Blocked options are published too, with the
 * reason, so a client can show a greyed button that explains itself rather
 * than hiding the step and leaving somebody hunting for it.
 */
export const jobTransitionOptionSchema = z.object({
    to: jobStatusSchema,
    allowed: z.boolean(),
    reason: z.string().nullable(),
});
export const jobSchema = z.object({
    id: idSchema,
    number: z.number().int(),
    title: z.string(),
    description: z.string().nullable(),
    status: jobStatusSchema,
    priority: jobPrioritySchema,
    customerId: idSchema,
    propertyId: idSchema,
    assignedTechId: idSchema.nullable(),
    scheduledStart: isoDateTime.nullable(),
    scheduledEnd: isoDateTime.nullable(),
    enRouteAt: isoDateTime.nullable(),
    startedAt: isoDateTime.nullable(),
    completedAt: isoDateTime.nullable(),
    /**
     * Customer sign-off.
     *
     * The name and the time come back on every job, so a list can say "signed"
     * without fetching files. `signatureUrl` is null unless the query loaded
     * the attachment — null here means "not loaded or not signed", and the two
     * are told apart by `signedAt`.
     */
    signedByName: z.string().nullable().default(null),
    signedAt: isoDateTime.nullable().default(null),
    signatureUrl: z.string().nullable().default(null),
    publicToken: z.string().nullable(),
    leadSourceId: idSchema.nullable(),
    leadSourceName: z.string().nullable().optional(),
    jobTypeId: idSchema.nullable(),
    jobTypeName: z.string().nullable().optional(),
    internalDiscountCents: nonNegativeCents,
    version: z.number().int(),
    createdAt: isoDateTime,
    updatedAt: isoDateTime,
    customer: customerSchema.omit({ properties: true }).partial().extend({ id: idSchema }).optional(),
    property: propertySchema.optional(),
    assignedTech: publicUserSchema.nullish(),
    lineItems: z.array(lineItemSchema).optional(),
    notes: z.array(noteSchema).optional(),
    attachments: z.array(attachmentSchema).optional(),
    statusEvents: z.array(jobStatusEventSchema).optional(),
    /** Office-only; the API omits it for a technician. */
    costItems: z.array(jobCostItemSchema).optional(),
    invoiceId: idSchema.nullish(),
    /**
     * The invoice's own numbers, carried on the job for anyone who has the job
     * and not the invoice — the technician's phone, which caches jobs and
     * cannot fetch an invoice in a basement.
     *
     * `balanceDueCents` is what is still owed, which is the only figure it is
     * safe to offer to collect. `totals` below is the job's ticket, which is a
     * different thing: it ignores discounts and payments, and once an invoice
     * exists it is history rather than the bill.
     */
    invoice: jobInvoiceSummarySchema.optional(),
    /** Quotes raised at this job. Published with a single job, not list rows. */
    estimates: z.array(jobEstimateSummarySchema).optional(),
    estimateId: idSchema.nullish(),
    totals: z
        .object({
        subtotalCents: centsSchema,
        taxCents: centsSchema,
        totalCents: centsSchema,
    })
        .optional(),
    /** Published with a single job and after a status change; absent from list rows. */
    availableTransitions: z.array(jobTransitionOptionSchema).optional(),
});
export const jobInputSchema = z.object({
    title: z.string().min(1).max(200),
    description: z.string().max(8000).nullish(),
    customerId: idSchema,
    propertyId: idSchema,
    priority: jobPrioritySchema.default('normal'),
    assignedTechId: idSchema.nullish(),
    scheduledStart: isoDateTime.nullish(),
    scheduledEnd: isoDateTime.nullish(),
    leadSourceId: idSchema.nullish(),
    jobTypeId: idSchema.nullish(),
    internalDiscountCents: nonNegativeCents.optional(),
    lineItems: z.array(lineItemInputSchema).optional(),
});
export const jobUpdateSchema = jobInputSchema.partial().omit({ lineItems: true });
export const jobListQuerySchema = paginationSchema.extend({
    from: isoDateTime.optional(),
    to: isoDateTime.optional(),
    techId: idSchema.optional(),
    customerId: idSchema.optional(),
    status: z.union([jobStatusSchema, z.array(jobStatusSchema)]).optional(),
    q: z.string().optional(),
    /** 'unscheduled' pulls the backlog rail on the dispatch board. */
    unscheduledOnly: z.coerce.boolean().optional(),
    /**
     * How the list comes back.
     *
     * 'schedule' — soonest first, which is what a board or a day's work wants,
     * and stays the default so nothing that reads this endpoint today changes.
     * 'newest' — most recently raised first, which is what a phone wants: the
     * job you just created is the one you are looking for, and on the schedule
     * ordering it is buried wherever its date happens to fall.
     *
     * Sorted by the database, not the caller: a page of a hundred sorted after
     * the fact is a hundred of the WRONG jobs put in the right order.
     */
    sort: z.enum(['schedule', 'newest']).default('schedule'),
});
export const jobStatusChangeSchema = z.object({
    status: jobStatusSchema,
    note: z.string().max(2000).nullish(),
    source: z.enum(['web', 'mobile', 'system', 'offline_sync']).default('web'),
    clientCreatedAt: isoDateTime.optional(),
});
export const jobScheduleSchema = z.object({
    assignedTechId: idSchema.nullable(),
    scheduledStart: isoDateTime.nullable(),
    scheduledEnd: isoDateTime.nullable(),
    /** Dispatcher explicitly accepted a double-booking. */
    allowConflict: z.boolean().default(false),
    /** Optimistic concurrency: the version the board was showing. */
    expectedVersion: z.number().int().optional(),
});
export const conflictSchema = z.object({
    id: idSchema,
    number: z.number().int().optional(),
    title: z.string().optional(),
    start: isoDateTime,
    end: isoDateTime,
    overlapMinutes: z.number().int(),
});
// --- estimates --------------------------------------------------------------
export const estimateSchema = z.object({
    id: idSchema,
    number: z.number().int(),
    status: estimateStatusSchema,
    customerId: idSchema,
    propertyId: idSchema,
    /** Who quoted it. Null on anything raised before this was recorded. */
    createdById: idSchema.nullable().default(null),
    title: z.string(),
    notes: z.string().nullable(),
    taxRateBps: bpsSchema,
    discountType: discountTypeSchema,
    discountValueCents: centsSchema,
    discountValueBps: bpsSchema,
    depositType: depositTypeSchema.default('none'),
    depositValueCents: centsSchema.default(0),
    depositValueBps: bpsSchema.default(0),
    /**
     * The deposit worked out, not the rule that produced it.
     *
     * `paidCents` is null when the deposit invoice was not loaded — a list
     * query has not looked, and saying zero would be a claim it cannot make.
     */
    deposit: z
        .object({
        required: z.boolean(),
        amountCents: centsSchema,
        invoiceId: idSchema.nullable(),
        invoiceNumber: z.number().int().nullable(),
        paidCents: centsSchema.nullable(),
        state: z.enum(['not_required', 'unpaid', 'partial', 'paid']),
    })
        .optional(),
    validUntil: isoDateTime.nullable(),
    sentAt: isoDateTime.nullable(),
    decidedAt: isoDateTime.nullable(),
    convertedJobId: idSchema.nullable(),
    /**
     * The job the technician was standing at when they quoted it.
     *
     * Distinct from `convertedJobId`, which is the job an APPROVED estimate
     * became. This one is where the conversation happened; that one is the work
     * it turned into. An estimate can have both, and they are different jobs.
     */
    jobId: idSchema.nullable().default(null),
    createdAt: isoDateTime,
    customer: customerSchema.partial().extend({ id: idSchema }).optional(),
    property: propertySchema.optional(),
    lineItems: z.array(lineItemSchema).optional(),
    totals: z.record(z.string(), z.any()).optional(),
});
export const estimateInputSchema = z.object({
    customerId: idSchema,
    propertyId: idSchema,
    /** The job it was quoted at, when it was quoted at one. */
    jobId: idSchema.nullish(),
    title: z.string().min(1).max(200),
    notes: z.string().max(8000).nullish(),
    taxRateBps: bpsSchema.optional(),
    discountType: discountTypeSchema.default('none'),
    discountValueCents: nonNegativeCents.default(0),
    discountValueBps: bpsSchema.default(0),
    depositType: depositTypeSchema.default('none'),
    depositValueCents: nonNegativeCents.default(0),
    depositValueBps: bpsSchema.default(0),
    validUntil: isoDateTime.nullish(),
    lineItems: z.array(lineItemInputSchema).default([]),
});
/**
 * What a company's payment setup looks like from outside.
 *
 * The connected account's id is deliberately absent: no screen needs it, and
 * an identifier that is never published is one that can never leak into a
 * log, a URL or a screenshot. What callers need is whether a card can be
 * taken, and that is a state, not an id.
 */
export const companyPaymentsSchema = z.object({
    state: z.enum(['not_connected', 'onboarding', 'restricted', 'enabled']),
    canCharge: z.boolean(),
    canPayout: z.boolean(),
    /** When the flags behind this were last refreshed from the processor. */
    syncedAt: isoDateTime.nullable(),
});
export const estimateStatusChangeSchema = z.object({ status: estimateStatusSchema });
export const estimateConvertSchema = z.object({
    title: z.string().max(200).optional(),
    assignedTechId: idSchema.nullish(),
    scheduledStart: isoDateTime.nullish(),
    scheduledEnd: isoDateTime.nullish(),
});
// --- invoices & payments ----------------------------------------------------
export const paymentSchema = z.object({
    id: idSchema,
    invoiceId: idSchema,
    amountCents: centsSchema,
    method: paymentMethodSchema,
    reference: z.string().nullable(),
    memo: z.string().nullable(),
    providerId: z.string(),
    providerRef: z.string().nullable(),
    receivedById: idSchema.nullable(),
    receivedByName: z.string().nullable(),
    voidedAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
});
export const paymentInputSchema = z.object({
    amountCents: centsSchema.min(1),
    method: paymentMethodSchema,
    reference: z.string().max(120).nullish(),
    memo: z.string().max(2000).nullish(),
    /** Allow deliberate overpayment; the UI must ask first. */
    allowOverpayment: z.boolean().default(false),
    clientCreatedAt: isoDateTime.optional(),
});
export const invoiceTotalsSchema = z.object({
    subtotalCents: centsSchema,
    taxableSubtotalCents: centsSchema,
    discountCents: centsSchema,
    taxableBaseCents: centsSchema,
    taxCents: centsSchema,
    totalCents: centsSchema,
    amountPaidCents: centsSchema,
    balanceDueCents: centsSchema,
    isSettled: z.boolean(),
});
export const invoiceSchema = z.object({
    id: idSchema,
    number: z.number().int(),
    status: invoiceStatusSchema,
    jobId: idSchema.nullable(),
    customerId: idSchema,
    propertyId: idSchema,
    taxRateBps: bpsSchema,
    discountType: discountTypeSchema,
    discountValueCents: centsSchema,
    discountValueBps: bpsSchema,
    issuedAt: isoDateTime.nullable(),
    dueAt: isoDateTime.nullable(),
    notes: z.string().nullable(),
    publicToken: z.string().nullable(),
    createdAt: isoDateTime,
    customer: customerSchema.partial().extend({ id: idSchema }).optional(),
    property: propertySchema.optional(),
    job: jobSchema.partial().extend({ id: idSchema }).optional(),
    lineItems: z.array(lineItemSchema).optional(),
    payments: z.array(paymentSchema).optional(),
    totals: invoiceTotalsSchema.optional(),
    paymentState: z.enum(['unpaid', 'partial', 'paid', 'overpaid']).optional(),
});
export const invoiceInputSchema = z.object({
    jobId: idSchema.optional(),
    customerId: idSchema.optional(),
    propertyId: idSchema.optional(),
    taxRateBps: bpsSchema.optional(),
    discountType: discountTypeSchema.optional(),
    discountValueCents: nonNegativeCents.optional(),
    discountValueBps: bpsSchema.optional(),
    dueInDays: z.number().int().min(0).max(365).optional(),
    notes: z.string().max(8000).nullish(),
    lineItems: z.array(lineItemInputSchema).optional(),
});
export const invoiceUpdateSchema = z.object({
    taxRateBps: bpsSchema.optional(),
    discountType: discountTypeSchema.optional(),
    discountValueCents: nonNegativeCents.optional(),
    discountValueBps: bpsSchema.optional(),
    dueAt: isoDateTime.nullish(),
    notes: z.string().max(8000).nullish(),
    status: invoiceStatusSchema.optional(),
    lineItems: z.array(lineItemInputSchema).optional(),
});
export const invoiceListQuerySchema = paginationSchema.extend({
    status: invoiceStatusSchema.optional(),
    customerId: idSchema.optional(),
    outstandingOnly: z.coerce.boolean().optional(),
});
// --- recurrence (P1) --------------------------------------------------------
export const recurrenceSchema = z.object({
    id: idSchema,
    customerId: idSchema,
    propertyId: idSchema,
    title: z.string(),
    frequency: recurrenceFrequencySchema,
    intervalDays: z.number().int().nullable(),
    /** 0 = Sunday. Used by weekly/biweekly. */
    dayOfWeek: z.number().int().min(0).max(6).nullable(),
    dayOfMonth: z.number().int().min(1).max(31).nullable(),
    startHour: z.number().int().min(0).max(23),
    durationMinutes: z.number().int().min(15),
    assignedTechId: idSchema.nullable(),
    startsOn: isoDateTime,
    endsOn: isoDateTime.nullable(),
    active: z.boolean(),
    lastGeneratedThrough: isoDateTime.nullable(),
    createdAt: isoDateTime,
});
export const recurrenceInputSchema = z.object({
    customerId: idSchema,
    propertyId: idSchema,
    title: z.string().min(1).max(200),
    frequency: recurrenceFrequencySchema,
    intervalDays: z.number().int().min(1).max(365).nullish(),
    dayOfWeek: z.number().int().min(0).max(6).nullish(),
    dayOfMonth: z.number().int().min(1).max(31).nullish(),
    startHour: z.number().int().min(0).max(23).default(9),
    durationMinutes: z.number().int().min(15).max(720).default(120),
    assignedTechId: idSchema.nullish(),
    startsOn: isoDateTime,
    endsOn: isoDateTime.nullish(),
    lineItems: z.array(lineItemInputSchema).default([]),
});
export const recurrenceGenerateSchema = z.object({
    through: isoDateTime.optional(),
    horizonDays: z.number().int().min(1).max(365).default(60),
});
// --- devices & push ---------------------------------------------------------
export const deviceRegisterSchema = z.object({
    platform: z.enum(['ios', 'android', 'web']),
    pushToken: z.string().min(1),
    name: z.string().max(120).optional(),
});
// --- sync -------------------------------------------------------------------
export const queuedMutationSchema = z.object({
    id: z.string().min(1),
    seq: z.number().int(),
    kind: z.enum(MUTATION_KINDS),
    jobId: idSchema,
    createdAt: isoDateTime,
    payload: z.record(z.string(), z.any()).default({}),
});
export const syncPushSchema = z.object({
    mutations: z.array(queuedMutationSchema).max(200),
});
export const syncOutcomeSchema = z.object({
    id: z.string(),
    seq: z.number().int(),
    jobId: idSchema,
    kind: z.enum(MUTATION_KINDS),
    decision: z.enum(['apply', 'duplicate', 'superseded', 'conflict', 'reject']),
    reason: z.string().optional(),
    resultingStatus: jobStatusSchema.optional(),
});
export const syncPushResponseSchema = z.object({
    outcomes: z.array(syncOutcomeSchema),
    /** Fresh copies of every job the batch touched, post-apply. */
    jobs: z.array(jobSchema),
    serverTime: isoDateTime,
});
export const syncPullQuerySchema = z.object({
    /** See FIELD_CACHE_DAYS: a technician's real horizon, not an arbitrary two. */
    days: z.coerce
        .number()
        .int()
        .min(1)
        .max(FIELD_CACHE_MAX_DAYS)
        .default(FIELD_CACHE_DAYS),
    /** How far back to include. See FIELD_CACHE_BACK_DAYS. */
    back: z.coerce
        .number()
        .int()
        .min(0)
        .max(FIELD_CACHE_MAX_DAYS)
        .default(FIELD_CACHE_BACK_DAYS),
    since: isoDateTime.optional(),
});
export const syncPullResponseSchema = z.object({
    jobs: z.array(jobSchema),
    serverTime: isoDateTime,
    /** Windows the cache covers, so the app can say "cached through Thursday". */
    from: isoDateTime,
    to: isoDateTime,
});
// --- reports (P1) -----------------------------------------------------------
export const reportRangeSchema = z.object({
    from: isoDateTime.optional(),
    to: isoDateTime.optional(),
    months: z.coerce.number().int().min(1).max(36).default(12),
});
export const revenueReportSchema = z.object({
    months: z.array(z.object({
        month: z.string(),
        invoicedCents: centsSchema,
        collectedCents: centsSchema,
        jobCount: z.number().int(),
    })),
    totalInvoicedCents: centsSchema,
    totalCollectedCents: centsSchema,
});
export const jobsByStatusReportSchema = z.object({
    counts: z.array(z.object({ status: jobStatusSchema, count: z.number().int() })),
    total: z.number().int(),
});
export const receivablesReportSchema = z.object({
    buckets: z.array(z.object({
        bucket: z.enum(['current', '1_30', '31_60', '61_90', '90_plus']),
        count: z.number().int(),
        totalCents: centsSchema,
    })),
    invoices: z.array(z.object({
        id: idSchema,
        number: z.number().int(),
        customerName: z.string(),
        dueAt: isoDateTime.nullable(),
        daysOverdue: z.number().int(),
        balanceCents: centsSchema,
    })),
    totalOutstandingCents: centsSchema,
});
// --- telephony --------------------------------------------------------------
export const callDirectionSchema = z.enum(CALL_DIRECTIONS);
export const callStatusSchema = z.enum(CALL_STATUSES);
export const messageDirectionSchema = z.enum(MESSAGE_DIRECTIONS);
export const messageStatusSchema = z.enum(MESSAGE_STATUSES);
export const callSchema = z.object({
    id: idSchema,
    providerId: z.string(),
    providerCallId: z.string(),
    direction: callDirectionSchema,
    status: callStatusSchema,
    fromNumber: z.string(),
    toNumber: z.string(),
    customerId: idSchema.nullable(),
    jobId: idSchema.nullable(),
    userId: idSchema.nullable(),
    startedAt: isoDateTime,
    answeredAt: isoDateTime.nullable(),
    endedAt: isoDateTime.nullable(),
    durationSeconds: z.number().int().nullable(),
    recordingUrl: z.string().nullable(),
    voicemailUrl: z.string().nullable(),
    transcript: z.string().nullable(),
    notes: z.string().nullable(),
    customer: z
        .object({ id: idSchema, name: z.string(), phone: z.string().nullable() })
        .nullable()
        .optional(),
    job: z
        .object({
        id: idSchema,
        number: z.number().int(),
        title: z.string(),
        status: jobStatusSchema,
        scheduledStart: isoDateTime.nullable(),
    })
        .nullable()
        .optional(),
    handledBy: z.object({ id: idSchema, name: z.string() }).nullable().optional(),
    createdAt: isoDateTime,
});
// --- payment requests -------------------------------------------------------
export const paymentRequestStatusSchema = z.enum([
    'pending',
    'processing',
    'paid',
    'failed',
    'cancelled',
    'expired',
]);
export const paymentRequestChannelSchema = z.enum(['link', 'terminal', 'virtual_terminal']);
export const paymentRequestSchema = z.object({
    id: idSchema,
    invoiceId: idSchema,
    invoiceNumber: z.number().int().nullable(),
    status: paymentRequestStatusSchema,
    channel: paymentRequestChannelSchema,
    amountCents: z.number().int(),
    currency: z.string(),
    /** Always our own URL, so a link already sent stays good. */
    url: z.string(),
    providerId: z.string(),
    /** "Visa ending 4242", once something has been charged. Never more. */
    instrumentLabel: z.string().nullable(),
    failureReason: z.string().nullable(),
    settledCents: z.number().int().nullable(),
    overpaid: z.boolean(),
    jobId: idSchema.nullable(),
    requestedBy: z.object({ id: idSchema, name: z.string() }).nullable(),
    expiresAt: isoDateTime.nullable(),
    sentAt: isoDateTime.nullable(),
    paidAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
});
export const createPaymentRequestSchema = z.object({
    invoiceId: idSchema,
    /** Omit for the whole outstanding balance. */
    amountCents: z.number().int().positive().nullish(),
    channel: z.enum(['link', 'virtual_terminal']).default('link'),
    jobId: idSchema.nullish(),
});
export const messageSchema = z.object({
    id: idSchema,
    direction: messageDirectionSchema,
    status: messageStatusSchema,
    fromNumber: z.string(),
    toNumber: z.string(),
    customerId: idSchema.nullable(),
    jobId: idSchema.nullable(),
    body: z.string(),
    template: z.string().nullable(),
    sentAt: isoDateTime.nullable(),
    deliveredAt: isoDateTime.nullable(),
    failureReason: z.string().nullable(),
    customer: z.object({ id: idSchema, name: z.string() }).nullable().optional(),
    job: z.object({ id: idSchema, number: z.number().int(), title: z.string() }).nullable().optional(),
    sentBy: z.object({ id: idSchema, name: z.string() }).nullable().optional(),
    createdAt: isoDateTime,
});
export const clickToCallSchema = z.object({
    toNumber: z.string().min(3).max(40),
    customerId: idSchema.nullish(),
    jobId: idSchema.nullish(),
    /** Overrides the user's stored extension for this one call. */
    agentExtension: z.string().max(40).nullish(),
});
export const sendMessageSchema = z.object({
    toNumber: z.string().min(3).max(40).optional(),
    customerId: idSchema.nullish(),
    jobId: idSchema.nullish(),
    body: z.string().min(1).max(1600),
});
export const callListQuerySchema = paginationSchema.extend({
    customerId: idSchema.optional(),
    jobId: idSchema.optional(),
    direction: callDirectionSchema.optional(),
    status: callStatusSchema.optional(),
    /** Only calls nobody has dealt with — the missed-call queue. */
    unresolvedOnly: z.coerce.boolean().optional(),
    from: isoDateTime.optional(),
    to: isoDateTime.optional(),
});
export const callUpdateSchema = z.object({
    customerId: idSchema.nullish(),
    jobId: idSchema.nullish(),
    notes: z.string().max(4000).nullish(),
});
export const messageListQuerySchema = paginationSchema.extend({
    customerId: idSchema.optional(),
    jobId: idSchema.optional(),
});
/** What the screen-pop renders while the phone is still ringing. */
export const callerContextSchema = z.object({
    call: callSchema,
    customer: customerSchema.nullable(),
    /** Populated when one number belongs to more than one customer. */
    candidates: z.array(z.object({ id: idSchema, name: z.string(), phone: z.string().nullable() })),
    recentJobs: z.array(jobSchema.partial().extend({ id: idSchema })),
    outstandingCents: centsSchema,
    lastCallAt: isoDateTime.nullable(),
});
// --- public status page (P1) ------------------------------------------------
export const publicJobStatusSchema = z.object({
    companyName: z.string(),
    companyPhone: z.string().nullable(),
    jobNumber: z.number().int(),
    title: z.string(),
    status: jobStatusSchema,
    scheduledStart: isoDateTime.nullable(),
    scheduledEnd: isoDateTime.nullable(),
    enRouteAt: isoDateTime.nullable(),
    completedAt: isoDateTime.nullable(),
    address: z.string(),
    tech: z.object({ name: z.string(), colorHex: z.string().nullable() }).nullable(),
    updatedAt: isoDateTime,
});
// --- errors -----------------------------------------------------------------
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.any().optional(),
    }),
});
//# sourceMappingURL=schemas.js.map